/**
 * 自定义下拉选择器
 *
 * 替代原生 select，支持自定义下拉面板样式
 * 文字超出时悬停显示 tooltip
 */
import { useState, useEffect, useRef } from 'react'
import { ChevronDown, Check } from 'lucide-react'

interface Option {
  value: string
  label: string
}

interface CustomSelectProps {
  /** 当前选中值 */
  value?: string
  /** 选择变更回调 */
  onChange: (value: string) => void
  /** 选项列表 */
  options: Option[]
  /** 占位符 */
  placeholder?: string
  /** 是否禁用 */
  disabled?: boolean
  /** 自定义类名 */
  className?: string
  /** 错误状态（红色边框） */
  error?: boolean
}

export function CustomSelect({
  value,
  onChange,
  options,
  placeholder = '请选择',
  disabled = false,
  className = '',
  error = false,
}: CustomSelectProps) {
  const [open, setOpen] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [isOverflow, setIsOverflow] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLSpanElement>(null)

  // 获取选中项的标签
  const selectedOption = options.find((opt) => opt.value === value)
  const selectedLabel = selectedOption?.label || ''

  // 检测文字是否超出
  useEffect(() => {
    if (textRef.current) {
      const isTextOverflow = textRef.current.scrollWidth > textRef.current.clientWidth
      setIsOverflow(isTextOverflow)
    }
  }, [selectedLabel, open])

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // 选择选项
  const handleSelect = (optionValue: string) => {
    onChange(optionValue)
    setOpen(false)
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* 触发按钮 */}
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        onMouseEnter={() => isOverflow && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`w-full h-9 px-3 py-2 text-left text-sm border rounded-md transition-colors overflow-hidden
          ${disabled ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed' : 'bg-white hover:border-neutral-300'}
          ${open ? 'border-primary-500 ring-1 ring-primary-500' : error ? 'border-red-500 ring-1 ring-red-500' : 'border-neutral-200'}
          ${!selectedLabel ? 'text-neutral-400' : 'text-neutral-700'}
        `}
      >
        <span className="flex items-center justify-between gap-2">
          <span ref={textRef} className="truncate flex-1">{selectedLabel || placeholder}</span>
          <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {/* Tooltip 提示 */}
      {showTooltip && !open && isOverflow && (
        <div className="absolute z-[60] top-full mt-1 left-0 px-3 py-2 bg-neutral-800 text-white text-sm rounded-md shadow-lg whitespace-nowrap max-w-[300px] overflow-hidden">
          {selectedLabel}
        </div>
      )}

      {/* 下拉面板 */}
      {open && (
        <div className="absolute z-50 mt-1 bg-white border border-neutral-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
          style={{ minWidth: '100%', width: 'auto', maxWidth: '300px' }}>
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => handleSelect(option.value)}
              className={`px-3 py-2 cursor-pointer flex items-center justify-between gap-2 transition-colors whitespace-nowrap
                ${value === option.value ? 'bg-primary-50 text-primary-700 font-medium' : 'text-neutral-600 hover:bg-neutral-50'}
              `}
            >
              <span>{option.label}</span>
              {value === option.value && <Check className="w-4 h-4 text-primary-600 shrink-0" />}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default CustomSelect